import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';

const milestones = [
  { year: '2018', title: '项目创立', desc: '由数位历史文化研究者联合发起，专注于中国古代皇室礼制文化的研究与传播，以「太子千秋万载」为核心研究课题。' },
  { year: '2019', title: '首期研究成果发布', desc: '完成对先秦至隋唐时期储君祝颂文化的系统性梳理，发表《千秋万载考》系列研究论文，获得学界广泛关注。' },
  { year: '2020', title: '数字化平台上线', desc: '建立线上内容平台，将研究成果转化为面向公众的文化普及内容，首年即获得超过50万读者的关注。' },
  { year: '2021', title: '学术合作拓展', desc: '与国内多所高校历史系建立合作关系，开展「古代储君制度与祝颂文化」联合研究项目，出版专题论文集。' },
  { year: '2023', title: '影视文化研究专项', desc: '启动「影视戏曲中的宫廷礼仪再现」专项研究，系统分析清宫剧与戏曲舞台上的储君祝颂文化的艺术表达。' },
  { year: '2025', title: '全新平台升级', desc: '平台全面升级改版，以更优质的阅读体验和更丰富的内容体系，持续为传统文化爱好者提供深度内容服务。' },
];

const team = [
  { name: '李文渊', role: '创始人 & 主编', bio: '北京大学历史学博士，专攻中国古代礼制史。著有《中国古代储君制度研究》《千秋万载：皇储祝颂文化考论》等学术著作。', icon: 'ri-user-star-line' },
  { name: '陈思古', role: '首席文化研究员', bio: '清华大学哲学系博士后，研究方向为中国古代政治哲学与儒家礼学。擅长从思想史角度解读皇室祝颂文化。', icon: 'ri-lightbulb-line' },
  { name: '王弘礼', role: '礼仪文化专家', bio: '故宫博物院研究员，长期从事清代宫廷礼仪制度研究。参与多项国家级文化遗产保护与复原项目。', icon: 'ri-building-line' },
  { name: '周影评', role: '影视文化评论员', bio: '知名影视评论人，专注于古装剧的历史真实性分析。在多家媒体开设「宫廷剧中的历史」专栏。', icon: 'ri-movie-line' },
];

const stats = [
  { value: '120+', label: '深度文章' },
  { value: '50万+', label: '累计读者' },
  { value: '4', label: '研究板块' },
  { value: '8年', label: '专注研究' },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9tckfrpv18futdmibc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, unknown>)?.message as string ||
          (parsed?.message as string) ||
          (parsed?.meta as Record<string, unknown>)?.detail as string ||
          responseText;
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 太子千秋万载文化研究',
    description: '太子千秋万载文化研究团队，专注中国古代皇储祝颂文化研究与传播。',
    url: 'https://yanxiya.cn/about',
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 太子千秋万载 | 皇储祝颂文化研究"
        description="太子千秋万载文化研究团队，专注中国古代皇储祝颂文化研究与传播。了解我们的品牌故事、研究历程与联系方式。"
        keywords="太子千秋万载,关于我们,皇太子祝颂礼仪,古代储君文化,文化研究"
        canonicalUrl="https://yanxiya.cn/about"
        ogType="website"
        schemaJson={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <section className="relative w-full h-64 md:h-80 flex items-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Traditional%20Chinese%20scholar%20studio%20with%20ancient%20books%20and%20scrolls%2C%20warm%20afternoon%20light%20streaming%20through%20wooden%20lattice%20windows%2C%20ink%20stone%20and%20brush%20on%20desk%2C%20serene%20cultural%20atmosphere%2C%20elegant%20classical%20Chinese%20aesthetic%2C%20rich%20wood%20tones%20and%20soft%20beige&width=1600&height=500&seq=about-hero&orientation=landscape"
              alt="关于我们"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-8">
              <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                  关于我们
                </h1>
                <p className="text-sm md:text-base text-background-200 max-w-2xl">
                  专注中国古代皇储祝颂文化研究，传承中华礼制文明的深厚底蕴
                </p>
              </div>
            </div>
          </section>

          <Breadcrumb items={[{ name: '关于我们' }]} />

          <section className="w-full px-4 md:px-8 py-10">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70"
                  >
                    <p className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                      {stat.value}
                    </p>
                    <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
                  品牌故事
                </h2>
                <div className="prose prose-lg max-w-none mx-auto">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    「太子千秋万载」文化研究项目，由一群热爱中国传统文化的学者于2018年联合发起。我们的初心源于一个简单的学术追问：为什么中国古代对皇太子的祝颂要使用「千秋万载」这四字？这背后究竟蕴含着怎样的文化密码？
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    从这个问题出发，我们展开了长达数年的系统性研究。研究范围从先秦的祝颂传统延伸到明清的礼仪制度，从宗庙祭祀的庄严场景延展到戏曲舞台的艺术再现。我们翻阅了大量正史典籍、礼制文献和考古资料，力求在学术严谨性与文化普及性之间取得最佳平衡。
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    今天，我们已经发展成为国内最专业的皇储祝颂文化研究平台之一。我们相信，那些镌刻在青史中的祝颂之辞，不仅是过往的礼仪程式，更是中华礼制文明的精神密码。我们的使命，就是让这些珍贵的文化遗产在当代焕发新的生命力。
                  </p>
                </div>
              </div>

              <div className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  发展历程
                </h2>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                  <div className="space-y-10">
                    {milestones.map((m, i) => (
                      <div
                        key={m.year}
                        className={`relative flex flex-col md:flex-row gap-4 md:gap-10 ${
                          i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        }`}
                      >
                        <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                          <div
                            className={`bg-background-100 rounded-lg border border-background-200/70 p-5 ml-12 md:ml-0 ${
                              i % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                            }`}
                          >
                            <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-accent-100 text-accent-700 mb-2">
                              {m.year}
                            </span>
                            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                              {m.title}
                            </h3>
                            <p className="text-sm text-foreground-600 leading-relaxed">
                              {m.desc}
                            </p>
                          </div>
                        </div>
                        <div className="hidden md:block w-40 flex-shrink-0"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mb-16">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  核心团队
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {team.map((member) => (
                    <div
                      key={member.name}
                      className="bg-background-100 rounded-lg border border-background-200/70 p-6 text-center"
                    >
                      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                        <i className={`${member.icon} text-xl text-primary-600`}></i>
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1">
                        {member.name}
                      </h3>
                      <p className="text-xs text-primary-600 font-medium mb-3">
                        {member.role}
                      </p>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {member.bio}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    如果您对「太子千秋万载」文化研究项目有任何问题、建议，或希望与我们开展合作，欢迎通过以下方式或表单与我们取得联系。我们期待与每一位热爱传统文化的朋友交流。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                        <p className="text-sm text-foreground-500">contact@taizi-qianqiu.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">办公地址</p>
                        <p className="text-sm text-foreground-500">北京市海淀区中关村文化大厦12层</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 flex-shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">联系电话</p>
                        <p className="text-sm text-foreground-500">+86 10-8888-6666</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 md:p-8">
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                      发送消息
                    </h3>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          name="subject"
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          消息内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                          placeholder="请输入您的消息（不超过500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                      </div>

                      <div className="honeypot-field">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      {formStatus === 'error' && (
                        <div className="px-4 py-3 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200">
                          {formError}
                        </div>
                      )}
                      {formStatus === 'success' && (
                        <div className="px-4 py-3 text-sm rounded-lg bg-green-50 text-green-700 border border-green-200">
                          消息已发送成功！我们会尽快回复您。
                        </div>
                      )}

                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-send-plane-line mr-2"></i>
                        发送消息
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}